import React, { useEffect, useLayoutEffect, useRef } from 'react';
import type { TableElement } from '@/components/canvas/document/publicTypes';
import {
  TABLE_BORDER_COLOR,
  TABLE_BORDER_WIDTH,
  TABLE_BORDER_STYLE,
  TABLE_CELL_PADDING_DEFAULT,
} from '@/components/canvas/shared/constants/legacyConfig';
import { firstStroke } from '@/components/canvas/elements/model';
import {
  getCell,
  getCellBackground,
  getCellTextStyle,
  getMergedInfo,
} from '@/components/canvas/elements/table/utils/tableUtilsIndex';

export type TableGridProps = {
  element: TableElement;
  rows: number;
  cols: number;
  rowHeights: number[];
  colWidths: number[];
  /** 当前处于编辑状态的单元格 key，格式 "row,col"；null 表示无编辑 */
  editingCellKey: string | null;
  onCellClick: (r: number, c: number, e: React.MouseEvent) => void;
  onCellDoubleClick: (r: number, c: number) => void;
  onCellTextCommit: (r: number, c: number, text: string) => void;
  onCellEditStart: (r: number, c: number) => void;
  onCellEditEnd: (r: number, c: number) => void;
};

/**
 * 单元格内容组件。
 * - isEditing=false：普通 div，不拦截事件，单击/双击正常冒泡到 <td>。
 * - isEditing=true：contenteditable div，进入时自动聚焦并将光标置末，
 *   阻止事件冒泡防止影响画布交互。
 */
const CellContent = React.memo(function CellContent({
  text,
  isEditing,
  onCommit,
  onEditStart,
  onEditEnd,
}: {
  text: string;
  isEditing: boolean;
  onCommit: (t: string) => void;
  onEditStart: () => void;
  onEditEnd: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const editingRef = useRef(false);

  // 当外部文本变化（如撤销）时同步 DOM，但编辑中不覆盖
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !editingRef.current && el.innerText !== text) {
      el.innerText = text;
    }
  });

  // 进入编辑态时自动聚焦，将光标置末
  useEffect(() => {
    if (isEditing && ref.current) {
      const el = ref.current;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [isEditing]);

  return (
    <div
      ref={ref}
      // 仅在编辑态启用 contenteditable
      contentEditable={isEditing || undefined}
      suppressContentEditableWarning
      style={{
        outline: 'none',
        // 不设 height:100%，保持自然高度，让 <td> 的 vertical-align 生效
        width: '100%',
        minHeight: '1em',
        cursor: isEditing ? 'text' : 'default',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
        wordBreak: 'normal',
        lineBreak: 'strict',
        // 非编辑态禁止用户选中文字，避免与画布选中态冲突
        userSelect: isEditing ? 'text' : 'none',
      }}
      onFocus={() => {
        if (!isEditing) return;
        editingRef.current = true;
        onEditStart();
      }}
      onBlur={(e) => {
        if (!isEditing) return;
        editingRef.current = false;
        onEditEnd();
        const newText = e.currentTarget.innerText;
        if (newText !== text) onCommit(newText);
      }}
      onKeyDown={(e) => {
        if (!isEditing) return;
        e.stopPropagation();
        if (e.key === 'Escape') e.currentTarget.blur();
      }}
      onMouseDown={(e) => {
        if (isEditing) e.stopPropagation();
      }}
      onClick={(e) => {
        if (isEditing) e.stopPropagation();
      }}
    />
  );
});

export function TableGrid({
  element,
  rows,
  cols,
  rowHeights,
  colWidths,
  editingCellKey,
  onCellClick,
  onCellDoubleClick,
  onCellTextCommit,
  onCellEditStart,
  onCellEditEnd,
}: TableGridProps) {
  const preset = element.data.borderPreset ?? 'full';
  const stroke = firstStroke(element.appearance);
  const borderW = stroke?.width ?? TABLE_BORDER_WIDTH;
  const borderC = stroke?.color ?? TABLE_BORDER_COLOR;
  const borderS = stroke?.style ?? TABLE_BORDER_STYLE;
  const padding = element.size.padding;
  const layoutWidth = colWidths.reduce((a, b) => a + b, 0);
  const layoutHeight = rowHeights.reduce((a, b) => a + b, 0);

  return (
    <table
      className="border-collapse"
      style={{ tableLayout: 'fixed', width: layoutWidth, height: layoutHeight }}
    >
      <tbody>
        {Array.from({ length: rows }, (_, r) => (
          <tr key={r} style={{ height: rowHeights[r] }}>
            {Array.from({ length: cols }, (_, c) => {
              const merged = getMergedInfo(element, r, c);
              if (merged === null) return null;
              const { rowSpan, colSpan, originR, originC } = merged;
              const cell = getCell(element, originR, originC);
              const cellKey = `${originR},${originC}`;
              const isEditing = editingCellKey === cellKey;
              const touchesTop = originR === 0;
              const touchesLeft = originC === 0;
              const touchesRight = originC + colSpan === cols;
              const touchesBottom = originR + rowSpan === rows;
              const mergedWidth = colWidths.slice(originC, originC + colSpan).reduce((a, b) => a + b, 0);
              const mergedHeight = rowHeights.slice(originR, originR + rowSpan).reduce((a, b) => a + b, 0);
              const borderStr = `${borderW}px ${borderS} ${borderC}`;
              let borderTop: string | 0 = 0;
              let borderLeft: string | 0 = 0;
              let borderRight: string | 0 = 0;
              let borderBottom: string | 0 = 0;
              if (preset === 'full') {
                borderTop = borderStr;
                borderLeft = borderStr;
                borderRight = borderStr;
                borderBottom = borderStr;
              } else if (preset === 'outerOnly') {
                if (touchesTop) borderTop = borderStr;
                if (touchesLeft) borderLeft = borderStr;
                if (touchesRight) borderRight = borderStr;
                if (touchesBottom) borderBottom = borderStr;
              } else if (preset === 'outerInnerH') {
                borderTop = touchesTop ? borderStr : 0;
                borderLeft = touchesLeft ? borderStr : 0;
                borderRight = touchesRight ? borderStr : 0;
                borderBottom = borderStr;
              } else if (preset === 'outerHInnerH') {
                if (touchesTop) borderTop = borderStr;
                borderBottom = borderStr;
              } else if (preset === 'outerInnerV') {
                borderTop = touchesTop ? borderStr : 0;
                borderLeft = touchesLeft ? borderStr : 0;
                borderRight = borderStr;
                borderBottom = touchesBottom ? borderStr : 0;
              } else if (preset === 'outerVInnerV') {
                if (touchesLeft) borderLeft = borderStr;
                borderRight = borderStr;
              }
              const textStyle = getCellTextStyle(element, originR, originC);
              const cellStyle: React.CSSProperties = {
                width: mergedWidth,
                height: mergedHeight,
                paddingTop: padding?.top ?? TABLE_CELL_PADDING_DEFAULT,
                paddingRight: padding?.right ?? TABLE_CELL_PADDING_DEFAULT,
                paddingBottom: padding?.bottom ?? TABLE_CELL_PADDING_DEFAULT,
                paddingLeft: padding?.left ?? TABLE_CELL_PADDING_DEFAULT,
                fontSize: textStyle.fontSize,
                fontWeight: textStyle.fontWeight,
                fontFamily: textStyle.fontFamily,
                fontStyle: textStyle.fontStyle,
                color: textStyle.color,
                lineHeight: textStyle.lineHeight,
                letterSpacing: textStyle.letterSpacing != null ? `${textStyle.letterSpacing}em` : undefined,
                textAlign: textStyle.textAlign,
                verticalAlign: textStyle.verticalAlign,
                overflow: 'hidden',
                ...(textStyle.writingMode && { writingMode: textStyle.writingMode }),
                backgroundColor: getCellBackground(element, originR, originC),
                borderTop,
                borderLeft,
                borderRight,
                borderBottom,
                // 编辑中显示 caret，让用户知道在哪里输入
                outline: isEditing ? '2px solid #4f87f6' : undefined,
                outlineOffset: isEditing ? '-2px' : undefined,
              };
              const cellText = cell?.text?.content ?? '';
              return (
                <td
                  key={c}
                  rowSpan={rowSpan}
                  colSpan={colSpan}
                  style={cellStyle}
                  onClick={(ev) => {
                    if (!isEditing) onCellClick(originR, originC, ev);
                  }}
                  onDoubleClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    onCellDoubleClick(originR, originC);
                  }}
                >
                  <CellContent
                    text={cellText}
                    isEditing={isEditing}
                    onCommit={(t) => onCellTextCommit(originR, originC, t)}
                    onEditStart={() => onCellEditStart(originR, originC)}
                    onEditEnd={() => onCellEditEnd(originR, originC)}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
